import React from "react";
import PropTypes from "prop-types";
import { Text } from "react-native";
import { Query } from "react-apollo";
import "intl";
import { addLocaleData, IntlProvider } from "react-intl";
import {
  LocaleLoader,
  BackButton,
  Router,
  Route,
  Switch,
  storage
} from "cci-utility";

import getConnectionInfoQuery from "../query/GetConnectionInfoQuery";
import AppLoader from "./AppLoader";

/**
 * Main app provider that sets up the Apollo Provider as well as the logic when the loader must be shown.
 *
 * Props:
 * publicRoutes: Routes that can be accessed when a successful authorization has not been performed.
 * loaderComponent: Component used to show the user that a connection is being made to the server.
 * globalComponents: Components used to show certain messages to the user, such as "Access Denied" messages.
 * useBackButton: Indicates if the Android native back button must be enabled for routes or not..
 * children: Routes that can be access once the user is successfully authenticated.
 */
class AppProvider extends React.Component {
  constructor(props) {
    super(props);
    // Loads the application locale data when the app is loaded
    props.loadLocaleData(addLocaleData);
  }

  render() {
    const {
      children,
      publicRoutes,
      loaderComponent,
      globalComponents,
      loadLocales,
      useBackButton,
      loginComponent: LoginPage,
      resetPasswordComponent: ResetPasswordPage,
      redirectOnSwitch
    } = this.props;

    const BackComponent = useBackButton === true ? BackButton : React.Fragment;

    return (
      <Query query={getConnectionInfoQuery} errorPolicy="none">
        {({ error, data: { ConnectionInfo: connectionInfo } }) => {
          if (error) {
            // TODO Find a better way to show error messages

            return <Text>{error.message}</Text>;
          }

          return (
            <LocaleLoader
              locale={connectionInfo.locale}
              loadLocales={loadLocales}
            >
              {messages => (
                <IntlProvider
                  locale={connectionInfo.locale}
                  key={connectionInfo.locale}
                  textComponent={Text}
                  messages={messages}
                >
                  <Router>
                    <BackComponent>
                      <AppLoader
                        authenticated={connectionInfo.authenticated}
                        loaderComponent={loaderComponent}
                        storage={storage}
                        connectionLost={connectionInfo.connectionLost}
                        redirectOnSwitch={redirectOnSwitch}
                      >
                        {/* User has been authenticated so we shown them the page corresponding to the URL */}
                        {connectionInfo.authenticated === true ? (
                          <Switch>
                            <Route path="/login">
                              <LoginPage storage={storage} />
                            </Route>

                            {/* Routes that can be accessed on successful authentication such as a dashboard */}
                            {children}
                          </Switch>
                        ) : (
                          <Switch>
                            {/* Routes that can be accessed without valid authentication such as forgot password */}
                            {publicRoutes}

                            {/* This is the route for the "reset password" link they receive via e-mail */}
                            <Route path="/reset-password/:token">
                              <ResetPasswordPage storage={storage} />
                            </Route>

                            {/* Since user is not authenticated, their "home page" is the login screen */}
                            <Route path="/">
                              <LoginPage storage={storage} />
                            </Route>
                          </Switch>
                        )}

                        {/* Components that must be added after the main components, used for generic error messages for example */}
                        {globalComponents(connectionInfo)}
                      </AppLoader>
                    </BackComponent>
                  </Router>
                </IntlProvider>
              )}
            </LocaleLoader>
          );
        }}
      </Query>
    );
  }
}

AppProvider.propTypes = {
  loginComponent: PropTypes.func.isRequired,
  resetPasswordComponent: PropTypes.func,
  useBackButton: PropTypes.bool,
  loaderComponent: PropTypes.node.isRequired,
  publicRoutes: PropTypes.node,
  children: PropTypes.node.isRequired,
  globalComponents: PropTypes.func.isRequired,
  loadLocales: PropTypes.func.isRequired,
  loadLocaleData: PropTypes.func.isRequired
};

AppProvider.defaultProps = {
  publicRoutes: [],
  resetPasswordComponent: () => {},
  useBackButton: true
};

export default AppProvider;
